import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLayout } from '../../../providers/Layout.provider';
import { useToken } from '../../../providers/Token.provider';
import { useBank } from '../../../providers/Bank.provider';
import BankConnection from './BankList/BankConnection';
import BankTypeContainer from './BankType/BankTypeContainer';
import styles from './BankConnectionContainer.module.scss';
import BankLoginList from './BankLoginList/BankLoginList';
import ManualBankAccountContainer from './ManualBankAccount/ManualBankAccountContainer';
import OnlineBankAccountContainer from './OnlineBankAccount/OnlineBankAccountContainer';
import BankConnectionTermsAndConditions from './BankConnectionTermsAndConditions/BankConnectionTermsAndConditions';

type FC = FunctionComponent;

const BankConnectionContainer: FC = () => {
  const { Button } = vp;
  const { t } = useTranslation('translations', { keyPrefix: 'BankConnection' });
  const { token, isSetByBrowser } = useToken();
  const { color, setLayoutInfo } = useLayout();
  const history = useHistory();
  const { BankingType, AccountType, FinancialInstitution, SubFiniancialIntistutions, setAccountType } = useBank();
  const [hasNoOnlineOption, setHasNoOnlineOption] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [shouldConnectBank, setShouldConnectBank] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const [isAdditionalFormCompleted, setIsAdditionalFormCompleted] = useState(false);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullName, setFullName] = useState('');

  const [selectedAccountService, setSelectedAccountService] = useState<number | null>(null);
  const hasBackButton = !(isSetByBrowser && token?.PaymentSelectionMethod === 'bank');
  const isBankTypeKnown = token?.AccountSelectionMethod !== 'any' && token?.AccountHolderType !== '';

  // set layout
  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      70,
      20,
      0,
      hasBackButton,
      true,
    );
  }, []);

  useEffect(() => {
    if (isTooltipVisible) {
      setTimeout(() => {
        setIsTooltipVisible(false);
      }, 3000);
    }
  }, [isTooltipVisible]);

  useEffect(() => {
    setAccountType(
      token?.AccountHolderType === 'business'
        ? 'business'
        : token?.AccountHolderType === 'individual'
          ? 'personal'
          : undefined,
    );
  }, [token]);

  useEffect(() => {
    let isButtonEnabledCheck = false;
    let shouldConnectBankCheck = false;

    if (hasNoOnlineOption) {
      isButtonEnabledCheck =
        token?.Country === 'US' && FinancialInstitution?.FIName === 'VOPAY TESTING BANK'
          ? isAdditionalFormCompleted
          : isFormCompleted;
    } else if (BankingType === 'manual' || token?.AccountSelectionMethod === 'manual') {
      isButtonEnabledCheck = isFormCompleted && FinancialInstitution !== undefined;
    } else if (BankingType === 'online' || token?.AccountSelectionMethod === 'online') {
      setIsFormCompleted(false);
      if (token && token?.Country === 'CA') {
        const isConditionMet =
          AccountType !== undefined &&
          FinancialInstitution !== undefined &&
          isAdditionalFormCompleted &&
          (selectedAccountService !== null || !SubFiniancialIntistutions?.length);

        isButtonEnabledCheck = isConditionMet;
        shouldConnectBankCheck = isConditionMet;
      } else if (token && token?.Country === 'US') {
        isButtonEnabledCheck =
          isAdditionalFormCompleted && AccountType !== undefined && FinancialInstitution !== undefined;
      }
    }
    setIsButtonEnabled(isButtonEnabledCheck);
    setShouldConnectBank(shouldConnectBankCheck);
  }, [
    isFormCompleted,
    isAdditionalFormCompleted,
    BankingType,
    AccountType,
    selectedAccountService,
    FinancialInstitution,
  ]);

  return (
    <div className={styles.bankConnectionContainer}>
      <BankConnection
        setHasNoOnlineOption={setHasNoOnlineOption}
        setSelectedAccountService={setSelectedAccountService}
        setAccessToken={setAccessToken}
      />
      {/* Account and Connection Type  */}
      {!isBankTypeKnown && <BankTypeContainer hasNoOnlineOption={hasNoOnlineOption} />}

      {/* Manual connection form */}
      {((!hasNoOnlineOption && token?.AccountSelectionMethod === 'manual') ||
        (!hasNoOnlineOption && token?.AccountSelectionMethod === 'any' && BankingType === 'manual') ||
        hasNoOnlineOption) && (
        <ManualBankAccountContainer
          shouldConnectBank={shouldConnectBank}
          isFormCompleted={isFormCompleted}
          setIsFormCompleted={setIsFormCompleted}
        />
      )}

      {/* US online connection additional information form */}
      {(token?.Country === 'US' || token?.Country === 'CA') &&
        !hasNoOnlineOption &&
        (BankingType === 'online' || token?.AccountSelectionMethod === 'online') && (
          <OnlineBankAccountContainer
            isButtonEnabled={isButtonEnabled}
            setIsAdditionalFormCompleted={setIsAdditionalFormCompleted}
            onlineBankFormValues={(data) => {
              setIdentificationNumber(data.IdentificationNumber);
              setPhoneNumber(data.PhoneNumber);
              if (AccountType === 'personal') {
                setFullName(`${data.FirstName} ${data.LastName}`);
              } else {
                setFullName(data.CompanyName);
              }
            }}
          />
        )}

      {/* Account Service (Associated banks) */}
      {(SubFiniancialIntistutions || []).length > 0 &&
        token?.Country === 'CA' &&
        (BankingType === 'online' ||
          (token?.AccountSelectionMethod === 'online' && FinancialInstitution && !hasNoOnlineOption)) && (
          <BankLoginList
            selectedAccountService={selectedAccountService}
            setSelectedAccountService={setSelectedAccountService}
          />
        )}

      <div className={styles.continueBtnContainer}>
        {isTooltipVisible && (
          <div className={styles.tooltipContainer}>
            <span>{t('tooltipMessage')}</span>
          </div>
        )}
        <BankConnectionTermsAndConditions
          showTermsAndConditions={showTermsAndConditions}
          setShowTermsAndConditions={() => setShowTermsAndConditions(false)}
          identificationNumber={identificationNumber}
          accessToken={accessToken}
          onNext={() => {
            setShowTermsAndConditions(false);

            if (BankingType === 'manual' || token?.AccountSelectionMethod === 'manual' || hasNoOnlineOption) {
              setShouldConnectBank(true);
            } else {
              history.push({
                pathname: `/connect/bank/${FinancialInstitution?.FinancialInstitutionID || 0}/online`,
                state: {
                  accessToken,
                  identificationNumber,
                  phoneNumber,
                  fullName,
                },
              });
            }
          }}
        />
        <Button
          id="connect-bank"
          label={t('continueButton') || ''}
          background={!isButtonEnabled ? '#C4C4C4' : color}
          textColor={!isButtonEnabled ? '#949494' : vp.getContrastColor(color)}
          opacity={isButtonEnabled ? '1' : '0.5'}
          width="100%"
          height="48px"
          fontUnit="px"
          textSize={16}
          round
          onClick={() => {
            if (isButtonEnabled) {
              setShowTermsAndConditions(true);
            } else {
              setIsTooltipVisible(true);
            }
          }}
        />
      </div>
    </div>
  );
};

export default BankConnectionContainer;
